import { Store } from '../../../reducers/store';
import { SECURITY_EVENT_TYPES } from '../constants';

/**
 * App State Integrity Detector
 * 
 * Monitors Redux state integrity, detects state corruption,
 * configuration tampering, and unexpected application behavior.
 */
export class AppStateIntegrityDetector {
    constructor(onSecurityEvent) {
        this.onSecurityEvent = onSecurityEvent;
        this.initialized = false;
        this.stateBaseline = null;
        this.stateHistory = [];
        this.configHistory = [];
        this.suspiciousStateChanges = [];
        this.lastStateCheck = Date.now();
        this.stateSubscription = null;
        this.initializationTime = Date.now(); // Track when RASP started
        this.appInitialized = false; // Track if app is fully initialized
    }

    /**
     * Initialize the app state integrity detector
     */
    async initialize() {
        try {
            // Create state baseline
            await this.createStateBaseline();
            
            // Set up state monitoring
            this.setupStateMonitoring();
            
            // Set up configuration monitoring
            this.setupConfigurationMonitoring();
            
            // Initialize state validation rules
            this.initializeValidationRules();
            
            this.initialized = true;
        } catch (error) {
            console.error('RASP - Failed to initialize App State Integrity Detector:', error);
            throw error;
        }
    }

    /**
     * Create baseline of application state
     */
    async createStateBaseline() {
        try {
            const currentState = Store.getState();
            
            this.stateBaseline = {
                timestamp: Date.now(),
                stateStructure: this.analyzeStateStructure(currentState),
                criticalFields: this.extractCriticalFields(currentState),
                stateSize: this.calculateStateSize(currentState),
                configStructure: this.analyzeConfigStructure(currentState.data?.config)
            };
        } catch (error) {
            console.warn('RASP - Failed to create state baseline:', error);
        }
    }

    /**
     * Set up state monitoring
     */
    setupStateMonitoring() {
        try {
            // Subscribe to Redux store changes
            this.stateSubscription = Store.subscribe(() => {
                this.monitorStateChange();
            });
            
        } catch (error) {
            console.warn('RASP - Failed to setup state monitoring:', error);
        }
    }

    /**
     * Set up configuration monitoring
     */
    setupConfigurationMonitoring() {
        // Configuration monitoring is handled through monitorConfigChange method
        // which is called from the main RASP service when config updates occur
    }

    /**
     * Initialize state validation rules
     */
    initializeValidationRules() {
        this.validationRules = {
            // Critical fields that should never be null/undefined unexpectedly
            // (only checked after app initialization and authentication)
            criticalFields: [
                'data.version',
                'data.authenticated'
                // Note: data.currentState can legitimately be null, so removed from critical fields
            ],
            
            // Fields that should maintain certain types
            typeValidation: {
                'data.loading': 'boolean',
                'data.authenticated': 'boolean',
                'data.connectionStatus': 'number',
                'data.bookingList': 'object',
                'data.config': 'object'
            },
            
            // Fields that should have specific value ranges
            rangeValidation: {
                'data.connectionStatus': { min: 0, max: 1 },
                'data.currentState': { values: [null, 0, 1, 2] }
            },
            
            // Configuration fields that are critical
            configCriticalFields: [
                'title',
                'device_id',
                'space_id',
                'location_id'
            ]
        };
    }

    /**
     * Perform app state integrity check
     */
    async performCheck() {
        if (!this.initialized) return;

        try {
            // Get current state
            const currentState = Store.getState();
            
            // Validate state structure
            await this.validateStateStructure(currentState);
            
            // Check for state corruption
            await this.checkStateCorruption(currentState);
            
            // Validate configuration integrity
            await this.validateConfigurationIntegrity(currentState);
            
            // Analyze state change patterns
            await this.analyzeStateChangePatterns();
            
            // Update state history
            this.updateStateHistory(currentState);
            
            this.lastStateCheck = Date.now();
            
        } catch (error) {
            console.warn('RASP - App state integrity check failed:', error);
            this.reportSecurityEvent(SECURITY_EVENT_TYPES.STATE_CORRUPTION, {
                message: 'App state integrity check failed',
                details: { error: error.message }
            });
        }
    }

    /**
     * Monitor state changes in real-time
     */
    monitorStateChange() {
        try {
            const currentState = Store.getState();
            const timestamp = Date.now();

            // Skip monitoring if state is not properly initialized yet
            if (!currentState || !currentState.data) {
                return; // State not ready yet
            }

            // Check if app is now fully initialized
            if (!this.appInitialized && currentState.data.version && currentState.data.authenticated !== undefined) {
                this.appInitialized = true;
                // console.log('RASP - App state fully initialized, enabling full monitoring');
            }

            // Only do quick validation if app is fully initialized
            if (this.appInitialized) {
                this.quickValidateState(currentState);
            }
            
            // Add to state history for pattern analysis
            this.stateHistory.push({
                timestamp,
                stateSnapshot: this.createStateSnapshot(currentState)
            });
            
            // Keep only recent history (last 100 changes)
            if (this.stateHistory.length > 100) {
                this.stateHistory = this.stateHistory.slice(-100);
            }
            
        } catch (error) {
            console.warn('RASP - State change monitoring failed:', error);
        }
    }

    /**
     * Monitor configuration changes
     */
    monitorConfigChange(configData) {
        try {
            const timestamp = Date.now();
            
            // Validate configuration structure
            this.validateConfigurationStructure(configData);
            
            // Check for suspicious configuration changes
            this.checkSuspiciousConfigChanges(configData);
            
            // Add to configuration history
            this.configHistory.push({
                timestamp,
                config: { ...configData }
            });
            
            // Keep only recent configuration history (last 50 changes)
            if (this.configHistory.length > 50) {
                this.configHistory = this.configHistory.slice(-50);
            }
            
        } catch (error) {
            console.warn('RASP - Configuration change monitoring failed:', error);
        }
    }

    /**
     * Analyze state structure
     */
    analyzeStateStructure(state) {
        try {
            const structure = {
                topLevelKeys: Object.keys(state),
                dataKeys: state.data ? Object.keys(state.data) : [],
                configKeys: state.data?.config ? Object.keys(state.data.config) : [],
                totalKeys: this.countAllKeys(state)
            };
            
            return structure;
        } catch (error) {
            return {};
        }
    }

    /**
     * Extract critical fields from state
     */
    extractCriticalFields(state) {
        const criticalFields = {};
        
        try {
            this.validationRules.criticalFields.forEach(fieldPath => {
                const value = this.getNestedValue(state, fieldPath);
                criticalFields[fieldPath] = value;
            });
        } catch (error) {
            console.warn('RASP - Failed to extract critical fields:', error);
        }
        
        return criticalFields;
    }

    /**
     * Calculate state size (approximate)
     */
    calculateStateSize(state) {
        try {
            return JSON.stringify(state).length;
        } catch (error) {
            return 0;
        }
    }

    /**
     * Analyze configuration structure
     */
    analyzeConfigStructure(config) {
        if (!config) return {};
        
        try {
            return {
                keys: Object.keys(config),
                hasDeviceId: !!config.device_id,
                hasSpaceId: !!config.space_id,
                hasLocationId: !!config.location_id,
                keyCount: Object.keys(config).length
            };
        } catch (error) {
            return {};
        }
    }

    /**
     * Validate state structure against baseline
     */
    async validateStateStructure(currentState) {
        if (!this.stateBaseline) return;

        try {
            const currentStructure = this.analyzeStateStructure(currentState);
            const baseline = this.stateBaseline.stateStructure;
            
            // Check for missing top-level keys
            const missingKeys = baseline.topLevelKeys.filter(
                key => !currentStructure.topLevelKeys.includes(key)
            );
            
            if (missingKeys.length > 0) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.STATE_CORRUPTION, {
                    message: 'Missing top-level state keys detected',
                    details: { missingKeys }
                });
            }
            
            // Check for unexpected new keys
            const newKeys = currentStructure.topLevelKeys.filter(
                key => !baseline.topLevelKeys.includes(key)
            );
            
            if (newKeys.length > 0) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.STATE_CORRUPTION, {
                    message: 'Unexpected new state keys detected',
                    details: { newKeys }
                });
            }
            
            // Check for significant size changes
            const currentSize = this.calculateStateSize(currentState);
            const baselineSize = this.stateBaseline.stateSize;
            const sizeChange = Math.abs(currentSize - baselineSize) / baselineSize;
            
            if (sizeChange > 2.0) { // 200% size change
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.STATE_CORRUPTION, {
                    message: 'Significant state size change detected',
                    details: {
                        baselineSize,
                        currentSize,
                        changePercentage: (sizeChange * 100).toFixed(2)
                    }
                });
            }
            
        } catch (error) {
            console.warn('RASP - State structure validation failed:', error);
        }
    }

    /**
     * Check for state corruption
     */
    async checkStateCorruption(currentState) {
        try {
            // Validate critical field types
            Object.entries(this.validationRules.typeValidation).forEach(([fieldPath, expectedType]) => {
                const value = this.getNestedValue(currentState, fieldPath);
                const actualType = typeof value;
                
                if (value !== null && value !== undefined && actualType !== expectedType) {
                    this.reportSecurityEvent(SECURITY_EVENT_TYPES.STATE_CORRUPTION, {
                        message: `State field type corruption detected: ${fieldPath}`,
                        details: {
                            fieldPath,
                            expectedType,
                            actualType,
                            value: String(value).substring(0, 100)
                        }
                    });
                }
            });
            
            // Validate field ranges
            Object.entries(this.validationRules.rangeValidation).forEach(([fieldPath, range]) => {
                const value = this.getNestedValue(currentState, fieldPath);
                
                if (value !== null && value !== undefined) {
                    if (range.min !== undefined && value < range.min) {
                        this.reportSecurityEvent(SECURITY_EVENT_TYPES.STATE_CORRUPTION, {
                            message: `State field below minimum range: ${fieldPath}`,
                            details: { fieldPath, value, min: range.min }
                        });
                    }
                    
                    if (range.max !== undefined && value > range.max) {
                        this.reportSecurityEvent(SECURITY_EVENT_TYPES.STATE_CORRUPTION, {
                            message: `State field above maximum range: ${fieldPath}`,
                            details: { fieldPath, value, max: range.max }
                        });
                    }
                    
                    if (range.values && !range.values.includes(value)) {
                        this.reportSecurityEvent(SECURITY_EVENT_TYPES.STATE_CORRUPTION, {
                            message: `State field has invalid value: ${fieldPath}`,
                            details: { fieldPath, value, allowedValues: range.values }
                        });
                    }
                }
            });
            
        } catch (error) {
            console.warn('RASP - State corruption check failed:', error);
        }
    }

    /**
     * Quick validation of state during real-time monitoring
     */
    quickValidateState(currentState) {
        try {
            // Skip validation during app initialization (first 10 seconds)
            const timeSinceInit = Date.now() - (this.stateBaseline?.timestamp || Date.now());
            if (timeSinceInit < 10000) {
                return; // Allow null/undefined during startup
            }

            // Skip validation if app is not authenticated yet (normal during startup)
            if (!currentState.data?.authenticated) {
                return; // Don't validate critical fields before authentication
            }

            // Check critical fields are not null/undefined (only after app is initialized)
            this.validationRules.criticalFields.forEach(fieldPath => {
                const value = this.getNestedValue(currentState, fieldPath);

                // Allow currentState to be null during normal app flow
                if (fieldPath === 'data.currentState' && (value === null || value === 0)) {
                    return; // currentState can legitimately be null or 0
                }

                if (value === null || value === undefined) {
                    this.reportSecurityEvent(SECURITY_EVENT_TYPES.STATE_CORRUPTION, {
                        message: `Critical state field is null/undefined: ${fieldPath}`,
                        details: { fieldPath, timeSinceInit, authenticated: currentState.data?.authenticated }
                    });
                }
            });

        } catch (error) {
            console.warn('RASP - Quick state validation failed:', error);
        }
    }

    /**
     * Validate configuration integrity
     */
    async validateConfigurationIntegrity(currentState) {
        const config = currentState.data?.config;
        if (!config) return;

        try {
            // Check for missing critical configuration fields
            this.validationRules.configCriticalFields.forEach(field => {
                if (!config[field]) {
                    this.reportSecurityEvent(SECURITY_EVENT_TYPES.CONFIGURATION_TAMPERING, {
                        message: `Missing critical configuration field: ${field}`,
                        details: { field }
                    });
                }
            });
            
            // Validate configuration structure against baseline
            if (this.stateBaseline?.configStructure) {
                const currentConfigStructure = this.analyzeConfigStructure(config);
                const baseline = this.stateBaseline.configStructure;
                
                if (currentConfigStructure.keyCount < baseline.keyCount * 0.5) {
                    this.reportSecurityEvent(SECURITY_EVENT_TYPES.CONFIGURATION_TAMPERING, {
                        message: 'Significant configuration key loss detected',
                        details: {
                            baselineKeys: baseline.keyCount,
                            currentKeys: currentConfigStructure.keyCount
                        }
                    });
                }
            }
            
        } catch (error) {
            console.warn('RASP - Configuration integrity validation failed:', error);
        }
    }

    /**
     * Validate configuration structure
     */
    validateConfigurationStructure(configData) {
        try {
            if (!configData || typeof configData !== 'object') {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.CONFIGURATION_TAMPERING, {
                    message: 'Invalid configuration data structure',
                    details: { type: typeof configData }
                });
                return;
            }
            
            // Check for required fields
            const requiredFields = ['device_id'];
            const missingFields = requiredFields.filter(field => !configData[field]);
            
            if (missingFields.length > 0) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.CONFIGURATION_TAMPERING, {
                    message: 'Missing required configuration fields',
                    details: { missingFields }
                });
            }
            
        } catch (error) {
            console.warn('RASP - Configuration structure validation failed:', error);
        }
    }

    /**
     * Check for suspicious configuration changes
     */
    checkSuspiciousConfigChanges(configData) {
        if (this.configHistory.length === 0) return;

        try {
            const lastConfig = this.configHistory[this.configHistory.length - 1].config;
            const changes = [];
            
            // Check for changes in critical fields
            this.validationRules.configCriticalFields.forEach(field => {
                if (lastConfig[field] !== configData[field]) {
                    changes.push({
                        field,
                        oldValue: lastConfig[field],
                        newValue: configData[field]
                    });
                }
            });
            
            if (changes.length > 0) {
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.CONFIGURATION_TAMPERING, {
                    message: 'Critical configuration fields changed',
                    details: { changes }
                });
            }
            
        } catch (error) {
            console.warn('RASP - Suspicious configuration changes check failed:', error);
        }
    }

    /**
     * Analyze state change patterns
     */
    async analyzeStateChangePatterns() {
        if (this.stateHistory.length < 10) return;

        try {
            const recentChanges = this.stateHistory.slice(-20);
            
            // Check for rapid state changes (MUCH LESS AGGRESSIVE)
            const rapidChanges = recentChanges.filter((change, index) => {
                if (index === 0) return false;
                const timeDiff = change.timestamp - recentChanges[index - 1].timestamp;
                return timeDiff < 10; // Less than 10ms between changes (very aggressive)
            });

            if (rapidChanges.length > 50) { // Increased from 5 to 50
                this.reportSecurityEvent(SECURITY_EVENT_TYPES.UNEXPECTED_BEHAVIOR, {
                    message: 'Extremely rapid state changes detected',
                    details: { rapidChanges: rapidChanges.length }
                });
            }
            
        } catch (error) {
            console.warn('RASP - State change pattern analysis failed:', error);
        }
    }

    /**
     * Create state snapshot for history
     */
    createStateSnapshot(state) {
        try {
            return {
                criticalFields: this.extractCriticalFields(state),
                stateSize: this.calculateStateSize(state),
                configPresent: !!state.data?.config
            };
        } catch (error) {
            return {};
        }
    }

    /**
     * Update state history
     */
    updateStateHistory(currentState) {
        // This is handled in monitorStateChange method
    }

    /**
     * Get nested value from object using dot notation
     */
    getNestedValue(obj, path) {
        return path.split('.').reduce((current, key) => {
            return current && current[key] !== undefined ? current[key] : undefined;
        }, obj);
    }

    /**
     * Count all keys in nested object
     */
    countAllKeys(obj, count = 0) {
        try {
            if (obj && typeof obj === 'object') {
                count += Object.keys(obj).length;
                Object.values(obj).forEach(value => {
                    if (value && typeof value === 'object') {
                        count = this.countAllKeys(value, count);
                    }
                });
            }
        } catch (error) {
            // Ignore errors in counting
        }
        return count;
    }

    /**
     * Report a security event
     */
    reportSecurityEvent(eventType, eventData) {
        if (this.onSecurityEvent) {
            this.onSecurityEvent(eventType, eventData);
        }
    }

    /**
     * Get app state statistics
     */
    getStatistics() {
        return {
            baseline: this.stateBaseline,
            stateChanges: this.stateHistory.length,
            configChanges: this.configHistory.length,
            suspiciousChanges: this.suspiciousStateChanges.length,
            lastCheck: this.lastStateCheck
        };
    }

    /**
     * Cleanup the detector
     */
    cleanup() {
        try {
            // Unsubscribe from store
            if (this.stateSubscription) {
                this.stateSubscription();
                this.stateSubscription = null;
            }
            
            // Clear data
            this.stateHistory = [];
            this.configHistory = [];
            this.suspiciousStateChanges = [];
            this.initialized = false;
            
            console.log('RASP - App State Integrity Detector cleanup completed');
        } catch (error) {
            console.error('RASP - App State Integrity Detector cleanup failed:', error);
        }
    }
}
